import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { api } from "../api/client"
import type { AppNotification, Paged } from "../api/types"
import { useAuth } from "../auth/AuthContext"
import { useFamily } from "../family/FamilyContext"
import { initials, ROLE_LABELS } from "../lib/format"
import { canManageFamily } from "../lib/permissions"
import { useRealtime } from "../realtime/RealtimeBridge"
import { usePublicConfig } from "../config/PublicConfigContext"
import { Icon, type IconName } from "./Icon"
import { Badge, Button, ErrorState, LoadingState } from "./ui"

interface NavItem {
  to: string
  label: string
  icon: IconName
  mobile?: boolean
  admin?: boolean
  instance?: boolean
}

const navigation: NavItem[] = [
  { to: "/", label: "Главная", icon: "home", mobile: true },
  { to: "/tasks", label: "Задачи", icon: "check", mobile: true },
  { to: "/me", label: "Моё", icon: "sparkles", mobile: true },
  { to: "/family", label: "Семья", icon: "users", mobile: true },
  { to: "/notifications", label: "События", icon: "bell", mobile: true },
  { to: "/instance", label: "Система", icon: "shield", instance: true },
  { to: "/settings", label: "Настройки", icon: "settings" }
]

export function AppShell() {
  const { user, logout } = useAuth()
  const { families, activeFamily, setActiveFamilyId, loading, error, refetch } = useFamily()
  const { state } = useRealtime()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenu, setMobileMenu] = useState(false)

  const notifications = useQuery({
    queryKey: ["notifications", "badge"],
    queryFn: () => api.get<Paged<AppNotification>>("/notifications?limit=20&unreadOnly=true"),
    refetchInterval: state === "live" ? false : 60_000
  })
  const unread = notifications.data?.items.filter((item) => !item.readAt).length ?? 0

  if (loading) {
    return (
      <div className="boot-screen">
        <Brand />
        <div className="boot-card">
          <LoadingState rows={3} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="boot-screen">
        <Brand />
        <div className="boot-card">
          <ErrorState
            title="Не удалось загрузить семьи"
            message="Проверьте соединение и попробуйте ещё раз. Сессия и данные не потеряны."
            retry={() => void refetch()}
          />
          <Button variant="ghost" onClick={() => void logout()}>
            Выйти
          </Button>
        </div>
      </div>
    )
  }

  const hasSystemShell = !activeFamily && user?.systemRole === "INSTANCE_ADMIN"

  if (hasSystemShell && !["/instance", "/settings"].includes(location.pathname)) {
    return <Navigate to="/instance" replace />
  }

  if (hasSystemShell) {
    const systemNavigation = navigation.filter((item) => item.instance || item.to === "/settings")
    return (
      <div className="app-shell app-shell--system">
        <aside className={`sidebar ${mobileMenu ? "is-open" : ""}`}>
          <div className="sidebar__top">
            <Brand />
            <div className="family-switcher family-switcher--system">
              <span className="family-switcher__avatar">
                <Icon name="shield" size={18} />
              </span>
              <span className="family-switcher__copy">
                <strong>Управление системой</strong>
                <small>Без семейного контекста</small>
              </span>
            </div>
          </div>
          <nav className="sidebar__nav" aria-label="Системная навигация">
            {systemNavigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenu(false)}
                className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}
              >
                <Icon name={item.icon} size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="sidebar__footer">
            <NavLink to="/settings" className="profile-chip" onClick={() => setMobileMenu(false)}>
              <span className="avatar">{initials(user.displayName)}</span>
              <span>
                <strong>{user.displayName}</strong>
                <small>@{user.login}</small>
              </span>
              <Icon name="chevronRight" size={16} />
            </NavLink>
          </div>
        </aside>

        {mobileMenu ? (
          <button className="sidebar-scrim" aria-label="Закрыть меню" onClick={() => setMobileMenu(false)} />
        ) : null}

        <div className="app-main">
          <header className="mobile-topbar">
            <button className="mobile-menu-button" onClick={() => setMobileMenu(true)} aria-label="Открыть меню">
              <Icon name="menu" />
            </button>
            <div className="mobile-wordmark">
              <span>♥</span> Семья
            </div>
            <NavLink to="/settings" className="topbar-bell" aria-label="Настройки">
              <Icon name="settings" />
            </NavLink>
          </header>
          <main className="page-content">
            <Outlet />
          </main>
        </div>

        <nav className="bottom-nav bottom-nav--system" aria-label="Системная навигация на телефоне">
          {systemNavigation.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? "is-active" : "")}>
              <span className="bottom-nav__icon">
                <Icon name={item.icon} size={22} />
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    )
  }

  if (!activeFamily) {
    return (
      <div className="boot-screen">
        <Brand />
        <div className="onboarding-card">
          <span className="onboarding-card__emoji">🏡</span>
          <h1>Семья пока не выбрана</h1>
          <p>Примите приглашение или создайте новую семью при регистрации.</p>
          {notifications.data?.items.length ? (
            <div className="form-stack" role="status">
              <strong>Последние события аккаунта</strong>
              {notifications.data.items.slice(0, 3).map((notification) => (
                <div className="info-callout" key={notification.id}>
                  <span aria-hidden="true">🔔</span>
                  <div>
                    <strong>{notification.title}</strong>
                    {notification.body ? <p>{notification.body}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <div className="button-row">
            <Button onClick={() => navigate("/invite")}>Принять приглашение</Button>
            <Button variant="secondary" onClick={() => void logout()}>
              Выйти
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const familyPaused = activeFamily.status === "SUSPENDED"
  const pausedRouteAllowed =
    location.pathname === "/settings" || (location.pathname === "/instance" && user?.systemRole === "INSTANCE_ADMIN")
  if (familyPaused && !pausedRouteAllowed) {
    const availableFamilies = families.filter((family) => family.status !== "SUSPENDED")
    return (
      <div className="boot-screen">
        <Brand />
        <div className="onboarding-card">
          <span className="onboarding-card__emoji">⏸️</span>
          <h1>Семья «{activeFamily.name}» приостановлена</h1>
          <p>Операции и семейные данные временно недоступны. Выберите другую семью или обратитесь к администратору.</p>
          {notifications.data?.items.length ? (
            <div className="form-stack" role="status">
              <strong>Сообщение администратора</strong>
              {notifications.data.items.slice(0, 3).map((notification) => (
                <div className="info-callout" key={notification.id}>
                  <span aria-hidden="true">🔔</span>
                  <div>
                    <strong>{notification.title}</strong>
                    {notification.body ? <p>{notification.body}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {availableFamilies.length ? (
            <div className="form-stack">
              <strong>Доступные семьи</strong>
              {availableFamilies.map((family) => (
                <Button key={family.id} variant="secondary" onClick={() => setActiveFamilyId(family.id)}>
                  Открыть «{family.name}»
                </Button>
              ))}
            </div>
          ) : null}
          <div className="button-row">
            <Button variant="secondary" onClick={() => navigate("/settings")}>
              Настройки аккаунта
            </Button>
            {user?.systemRole === "INSTANCE_ADMIN" ? (
              <Button variant="secondary" onClick={() => navigate("/instance")}>
                Управление системой
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => void logout()}>
              Выйти
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const visibleNavigation = navigation.filter((item) => {
    if (item.admin && !canManageFamily(activeFamily.role)) return false
    if (item.instance && user?.systemRole !== "INSTANCE_ADMIN") return false
    return true
  })

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileMenu ? "is-open" : ""}`}>
        <div className="sidebar__top">
          <Brand />
          <div className="family-switcher">
            <span className="family-switcher__avatar">{initials(activeFamily.name)}</span>
            <span className="family-switcher__copy">
              <strong>{activeFamily.name}</strong>
              <small>{ROLE_LABELS[activeFamily.role]}</small>
            </span>
            {families.length > 1 ? <Icon name="chevronDown" size={17} /> : null}
            {families.length > 1 ? (
              <select
                aria-label="Выбрать семью"
                value={activeFamily.id}
                onChange={(event) => setActiveFamilyId(event.target.value)}
              >
                {families.map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </div>

        <nav className="sidebar__nav" aria-label="Основная навигация">
          {visibleNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMobileMenu(false)}
              className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}
            >
              <Icon name={item.icon} size={20} />
              <span>{item.label}</span>
              {item.to === "/notifications" && unread > 0 ? (
                <span className="nav-link__count">{unread > 9 ? "9+" : unread}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="realtime-state" title="Состояние обновлений">
            <span className={`realtime-state__dot is-${state}`} />
            {state === "live"
              ? "Обновления в реальном времени"
              : state === "offline"
                ? "Без сети"
                : "Подключаем обновления"}
          </div>
          <NavLink to="/settings" className="profile-chip" onClick={() => setMobileMenu(false)}>
            <span className="avatar">{initials(user?.displayName)}</span>
            <span>
              <strong>{user?.displayName}</strong>
              <small>@{user?.login}</small>
            </span>
            <Icon name="chevronRight" size={16} />
          </NavLink>
        </div>
      </aside>

      {mobileMenu ? (
        <button className="sidebar-scrim" aria-label="Закрыть меню" onClick={() => setMobileMenu(false)} />
      ) : null}

      <div className="app-main">
        <header className="mobile-topbar">
          <button className="mobile-menu-button" onClick={() => setMobileMenu(true)} aria-label="Открыть меню">
            <Icon name="menu" />
          </button>
          <div className="mobile-wordmark">
            <span>♥</span> Семья
          </div>
          <NavLink to="/notifications" className="topbar-bell" aria-label={`Уведомления: ${unread}`}>
            <Icon name="bell" />
            {unread > 0 ? <i>{unread > 9 ? "9+" : unread}</i> : null}
          </NavLink>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Навигация на телефоне">
        {visibleNavigation
          .filter((item) => item.mobile)
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => (isActive ? "is-active" : "")}
            >
              <span className="bottom-nav__icon">
                <Icon name={item.icon} size={22} />
                {item.to === "/notifications" && unread > 0 ? <i /> : null}
              </span>
              <span>{item.label === "Уведомления" ? "События" : item.label}</span>
            </NavLink>
          ))}
      </nav>
    </div>
  )
}

export function Brand() {
  const { instanceLabel } = usePublicConfig()
  return (
    <div className="brand">
      <span className="brand__mark">
        <span>♥</span>
      </span>
      <span className="brand__copy">
        <strong>Семья</strong>
        <small>всё важное рядом</small>
      </span>
      {instanceLabel ? <Badge tone="violet">{instanceLabel}</Badge> : null}
    </div>
  )
}
